import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Building, Wrench, MapPin, CheckCircle, ArrowRight, 
  Briefcase, Star, Camera, Award
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContractorSignup = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    services: [],
    service_cities: [],
    bio: '',
    website: '',
    years_experience: '',
    license_number: ''
  });

  const serviceOptions = [
    'Kitchen Remodel', 'Bathroom Remodel', 'Roofing', 'Landscaping', 
    'Flooring', 'Painting', 'Plumbing', 'Electrical', 'HVAC', 
    'Windows & Doors', 'Basement Finishing', 'Smart Home'
  ];

  const cityOptions = [
    { slug: 'toronto', name: 'Toronto' },
    { slug: 'vancouver', name: 'Vancouver' },
    { slug: 'calgary', name: 'Calgary' },
    { slug: 'ottawa', name: 'Ottawa' },
    { slug: 'montreal', name: 'Montreal' }
  ];

  const toggleService = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const toggleCity = (slug) => {
    setFormData(prev => ({
      ...prev,
      service_cities: prev.service_cities.includes(slug)
        ? prev.service_cities.filter(c => c !== slug)
        : [...prev.service_cities, slug]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please log in first');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contractors`, {
        ...formData,
        years_experience: parseInt(formData.years_experience) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Application submitted! We\'ll review it within 24-48 hours.');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Camera, title: 'Free Exposure', desc: 'Get featured in before/after content across our platform' },
    { icon: Briefcase, title: 'Steady Work Pipeline', desc: 'Access consistent project opportunities in your service area' },
    { icon: Star, title: 'Build Your Reputation', desc: 'Collect reviews and build trust with homeowners' },
    { icon: Award, title: 'Partner with Top Realtors', desc: 'Connect with our sponsor network for referrals' }
  ];

  return (
    <div className="min-h-screen bg-neutral-50">
      <Header />
      
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-neutral-900 to-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 font-medium px-4 py-2 rounded-full text-sm mb-6">
              <Wrench className="w-4 h-4" />
              For Contractors
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
              Join Our <span className="text-[#FF3B30]">Contractor Network</span>
            </h1>
            <p className="text-xl text-white/70">
              Fulfill giveaway projects, gain exposure, and build your reputation with homeowners and realtors.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6"
              >
                <div className="w-14 h-14 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-[#FF3B30]" />
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">{benefit.title}</h3>
                <p className="text-neutral-600 text-sm">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 shadow-sm border border-neutral-200"
          >
            <h2 className="text-2xl font-bold text-neutral-900 mb-6" style={{ fontFamily: 'Outfit' }}>
              Apply to Join
            </h2>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contractor-signup-form">
              {/* Step 1: Basic Info */}
              <div className="space-y-4">
                <h3 className="font-semibold text-neutral-700 flex items-center gap-2">
                  <Building className="w-5 h-5" />
                  Business Information
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name</Label>
                    <Input
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      placeholder="Premier Renovations Inc."
                      className="mt-1 rounded-xl"
                      required
                      data-testid="contractor-company"
                    />
                  </div>
                  <div>
                    <Label>Contact Name</Label>
                    <Input
                      value={formData.contact_name}
                      onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                      placeholder="John Smith"
                      className="mt-1 rounded-xl"
                      required
                      data-testid="contractor-contact"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contact@premier.com"
                      className="mt-1 rounded-xl"
                      required
                      data-testid="contractor-email"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      className="mt-1 rounded-xl"
                      required
                      data-testid="contractor-phone"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Years of Experience</Label>
                    <Input
                      type="number"
                      value={formData.years_experience}
                      onChange={(e) => setFormData({ ...formData, years_experience: e.target.value })}
                      placeholder="10"
                      className="mt-1 rounded-xl"
                      data-testid="contractor-experience"
                    />
                  </div>
                  <div>
                    <Label>License Number (optional)</Label>
                    <Input
                      value={formData.license_number}
                      onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                      placeholder="LIC-12345"
                      className="mt-1 rounded-xl"
                      data-testid="contractor-license"
                    />
                  </div>
                </div>

                <div>
                  <Label>Website (optional)</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://premierrenovations.com"
                    className="mt-1 rounded-xl"
                  />
                </div>

                <div>
                  <Label>About Your Business</Label>
                  <Textarea
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    placeholder="Tell us about your experience, specialties, and what sets you apart..."
                    className="mt-1 rounded-xl"
                    rows={4}
                  />
                </div>
              </div>

              {/* Step 2: Services */}
              <div className="space-y-4 pt-6 border-t border-neutral-200">
                <h3 className="font-semibold text-neutral-700 flex items-center gap-2">
                  <Wrench className="w-5 h-5" />
                  Services Offered
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {serviceOptions.map((service) => (
                    <label
                      key={service}
                      className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                        formData.services.includes(service)
                          ? 'border-[#FF3B30] bg-[#FF3B30]/5'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <Checkbox
                        checked={formData.services.includes(service)}
                        onCheckedChange={() => toggleService(service)}
                      />
                      <span className="text-sm">{service}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Step 3: Service Areas */}
              <div className="space-y-4 pt-6 border-t border-neutral-200">
                <h3 className="font-semibold text-neutral-700 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Service Areas
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {cityOptions.map((city) => (
                    <label
                      key={city.slug}
                      className={`flex items-center gap-2 p-3 rounded-xl border cursor-pointer transition-colors ${
                        formData.service_cities.includes(city.slug)
                          ? 'border-[#FF3B30] bg-[#FF3B30]/5'
                          : 'border-neutral-200 hover:border-neutral-300'
                      }`}
                    >
                      <Checkbox
                        checked={formData.service_cities.includes(city.slug)}
                        onCheckedChange={() => toggleCity(city.slug)}
                      />
                      <span className="text-sm">{city.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading || formData.services.length === 0 || formData.service_cities.length === 0}
                className="w-full h-12 bg-[#FF3B30] hover:bg-[#DC2626] text-white rounded-xl font-medium"
                data-testid="contractor-submit"
              >
                {loading ? 'Submitting...' : 'Submit Application'}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>

              <p className="text-xs text-neutral-500 text-center">
                Applications are reviewed within 24-48 hours. We'll contact you to verify your credentials.
              </p>
            </form>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ContractorSignup;
